package com.evoapps.evoshift.data.mapper

import com.evoapps.evoshift.data.dto.roster.AvailabilityDto
import com.evoapps.evoshift.data.dto.roster.SharedAvailabilityDto
import com.evoapps.evoshift.domain.model.SharedAvailability
import com.evoapps.evoshift.data.dto.roster.WeeklyAvailabilityTemplateDto
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.WeeklyAvailabilityTemplate

/** SH-173: `availability/{id}.source` of a month given through the e-mailed link. */
const val EMAIL_LINK_SOURCE = "EMAIL_LINK"

class AvailabilityMapper(
    private val availabilityDayMapper: AvailabilityDayMapper
) {

    fun toDomain(dto: AvailabilityDto): Availability {
        return Availability(
            userId = dto.userId,
            rosterId = dto.rosterId,
            workplaceId = dto.workplaceId,
            month = dto.month,
            isLocked = dto.isLocked,
            isFilled = dto.isFilled,
            days = dto.days.entries.associate { (dayStr, dayDto) ->
                dayStr.toInt() to availabilityDayMapper.toDomain(dayDto)
            },
            viaEmailLink = dto.source == EMAIL_LINK_SOURCE
        )
    }

    fun toDto(domain: Availability): AvailabilityDto {
        return AvailabilityDto(
            userId = domain.userId,
            rosterId = domain.rosterId,
            workplaceId = domain.workplaceId,
            month = domain.month,
            isLocked = domain.isLocked,
            isFilled = domain.isFilled,
            days = domain.days.entries.associate { (dayInt, dayPref) ->
                dayInt.toString() to availabilityDayMapper.toDto(dayPref)
            },
            source = if (domain.viaEmailLink) EMAIL_LINK_SOURCE else null
        )
    }

    fun toDomainTemplate(dto: WeeklyAvailabilityTemplateDto): WeeklyAvailabilityTemplate {
        return WeeklyAvailabilityTemplate(
            id = dto.id,
            name = dto.name,
            userId = dto.userId,
            workplaceId = dto.workplaceId,
            days = dto.days.entries.associate { (dayStr, dayDto) ->
                dayStr.toInt() to availabilityDayMapper.toDomain(dayDto)
            }
        )
    }

    fun toDtoTemplate(domain: WeeklyAvailabilityTemplate): WeeklyAvailabilityTemplateDto {
        return WeeklyAvailabilityTemplateDto(
            id = domain.id,
            name = domain.name,
            userId = domain.userId,
            workplaceId = domain.workplaceId,
            days = domain.days.entries.associate { (dayInt, dayPref) ->
                dayInt.toString() to availabilityDayMapper.toDto(dayPref)
            }
        )
    }

    fun toDomain(dto: SharedAvailabilityDto): SharedAvailability = SharedAvailability(
        userId = dto.userId,
        month = dto.month,
        days = dto.days.entries.mapNotNull { (day, d) -> day.toIntOrNull()?.let { it to availabilityDayMapper.toDomain(d) } }.toMap(),
        isFilled = dto.isFilled,
        releasedToOwnerIds = dto.releasedToOwnerIds.toSet(),
        updatedAt = dto.updatedAt
    )

    fun toDto(domain: SharedAvailability): SharedAvailabilityDto = SharedAvailabilityDto(
        userId = domain.userId,
        month = domain.month,
        isFilled = domain.isFilled,
        releasedToOwnerIds = domain.releasedToOwnerIds.sorted(),
        updatedAt = domain.updatedAt,
        days = domain.days.entries.associate { (day, d) -> day.toString() to availabilityDayMapper.toDto(d) }
    )
}
